import React, {useRef, useCallback, useMemo, useState} from 'react'
import {Box, Flex, HStack, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import type SwiperType from 'swiper'
import {ProductImage} from '_uiComponents'
import type {MediaItem} from '../../index'

export interface ZoomImageModalProps {
    mediaItems: MediaItem[]
    initialIndex: number
    productName: string
}

/**
 * Inner content for the PDP zoom modal. Rendered as `body` via openModal().
 * The Modal shell (overlay, close button) is provided by ModalContext.
 */
const ZoomImageModal: React.FC<ZoomImageModalProps> = ({mediaItems, initialIndex, productName}) => {
    const styles = useMultiStyleConfig('ZoomImageModal', {}) as Record<string, StackProps>
    const swiperRef = useRef<{swiper: SwiperType}>(null)
    // Only images are shown in the zoom modal; video items are excluded.
    const imageItems = useMemo(() => mediaItems.filter((item) => item.type === 'image'), [mediaItems])
    const adjustedInitialIndex = useMemo(() => {
        const initialItem = mediaItems[initialIndex]
        const matchedIndex = initialItem ? imageItems.indexOf(initialItem) : -1
        return matchedIndex >= 0 ? matchedIndex : 0
    }, [mediaItems, imageItems, initialIndex])
    const [activeIndex, setActiveIndex] = useState(adjustedInitialIndex)
    // Keep track of which slides have ever been activated so their images
    // stay mounted once loaded (avoids re-downloading on re-visit).
    const [loadedIndices, setLoadedIndices] = useState(() => new Set([adjustedInitialIndex]))

    const goToSlide = useCallback((index: number) => {
        swiperRef.current?.swiper?.slideTo(index)
        setActiveIndex(index)
        setLoadedIndices((prev) => {
            if (prev.has(index)) return prev
            const next = new Set(prev)
            next.add(index)
            return next
        })
    }, [])

    return (
        <HStack className="cc-zoom-modal-thumbnails-container" sx={styles.zoomModalLayout}>
            {/* Thumbnail strip */}
            <Flex sx={{...styles.zoomModalThumbnailsContainer}}>
                <Box sx={{...styles.zoomModalThumbnails}}>
                    {imageItems.map((item, index) => (
                        <Box
                            key={`thumb-${index}`}
                            className={index === activeIndex ? 'active' : undefined}
                            sx={styles.zoomModalThumbnail}
                            onClick={() => goToSlide(index)}
                            role="button"
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={index === activeIndex ? 'true' : undefined}
                        >
                            <ProductImage
                                productImage={item.urlSwatch!}
                                alt={item.alt || productName}
                                isTileVideo={false}
                                styles={{
                                    imageWrapper: styles.zoomModalThumbnailImageWrapper,
                                    hoverImage: {},
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Flex>

            {/* Main enlarged image */}
            <Box sx={styles.zoomModalMain}>
                <Swiper
                    ref={swiperRef}
                    direction="horizontal"
                    slidesPerView={1}
                    initialSlide={adjustedInitialIndex}
                    modules={[]}
                    effect="fade"
                    fadeEffect={{crossFade: true}}
                    allowTouchMove={false}
                    simulateTouch={false}
                    keyboard={{enabled: false}}
                    noSwiping={true}
                    style={{width: '100%', height: '100%'}}
                >
                    {imageItems.map((item, index) => (
                        <SwiperSlide key={`zoom-image-${index}`}>
                            <Box sx={styles.zoomModalSlide}>
                                {loadedIndices.has(index) && (
                                    <ProductImage
                                        productImage={item.urlPdpZoom!}
                                        alt={item.alt || productName}
                                        isTileVideo={false}
                                        styles={{
                                            imageWrapper: styles.zoomModalImageWrapper,
                                            hoverImage: {},
                                        }}
                                    />
                                )}
                            </Box>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </HStack>
    )
}

export default ZoomImageModal
